import {
  CriteriaWeightsCriterion,
  CriteriaWeightsSubCriterion,
} from '../step-4-3-criteria-weights-overview.types';

export const TREE_NODE_HEIGHT = 48;
export const TREE_ROW_GAP = 12;
export const TREE_ROW_HEIGHT = TREE_NODE_HEIGHT + TREE_ROW_GAP;
export const ROOT_NODE_WIDTH = 220;
export const CRITERION_NODE_WIDTH = 300;
export const SUB_CRITERION_NODE_WIDTH = 320;

const TREE_COLUMN_GAP = 72;
const CRITERION_COLUMN_X = ROOT_NODE_WIDTH + TREE_COLUMN_GAP;
const SUB_CRITERION_COLUMN_X = CRITERION_COLUMN_X + CRITERION_NODE_WIDTH + TREE_COLUMN_GAP;

export interface CriteriaWeightsTreeBox {
  x: number;
  y: number;
  width: number;
}

export interface CriteriaWeightsTreeRootNode extends CriteriaWeightsTreeBox {
  weight: number | null;
}

export interface CriteriaWeightsTreeCriterionNode extends CriteriaWeightsTreeBox {
  criterion: CriteriaWeightsCriterion;
  expandable: boolean;
  expanded: boolean;
}

export interface CriteriaWeightsTreeSubCriterionNode extends CriteriaWeightsTreeBox {
  subCriterion: CriteriaWeightsSubCriterion;
}

export interface CriteriaWeightsTreeLink {
  key: string;
  path: string;
}

export interface CriteriaWeightsTreeLayout {
  root: CriteriaWeightsTreeRootNode;
  criteria: CriteriaWeightsTreeCriterionNode[];
  subCriteria: CriteriaWeightsTreeSubCriterionNode[];
  links: CriteriaWeightsTreeLink[];
  width: number;
  height: number;
}

interface CriterionBlock {
  node: CriteriaWeightsTreeCriterionNode;
  children: CriteriaWeightsTreeSubCriterionNode[];
  rowCount: number;
}

export function layoutCriteriaWeightsTree(
  criteria: CriteriaWeightsCriterion[],
  expandedCriteriaIds: ReadonlySet<number>,
): CriteriaWeightsTreeLayout {
  const blocks = placeCriterionBlocks(criteria, expandedCriteriaIds);
  const root = placeRoot(criteria, blocks);
  const rowCount = blocks.reduce((total, block) => total + block.rowCount, 0);
  const subCriteria = blocks.flatMap((block) => block.children);

  return {
    root,
    criteria: blocks.map((block) => block.node),
    subCriteria,
    links: blocks.flatMap((block) => [
      toLink(`criterion-${block.node.criterion.id}`, root, block.node),
      ...block.children.map((child) =>
        toLink(`sub-criterion-${child.subCriterion.id}`, block.node, child),
      ),
    ]),
    width:
      subCriteria.length > 0
        ? SUB_CRITERION_COLUMN_X + SUB_CRITERION_NODE_WIDTH
        : CRITERION_COLUMN_X + CRITERION_NODE_WIDTH,
    height: Math.max(rowCount, 1) * TREE_ROW_HEIGHT - TREE_ROW_GAP,
  };
}

function placeCriterionBlocks(
  criteria: CriteriaWeightsCriterion[],
  expandedCriteriaIds: ReadonlySet<number>,
): CriterionBlock[] {
  return criteria.reduce<CriterionBlock[]>((blocks, criterion) => {
    const firstRow = blocks.reduce((total, block) => total + block.rowCount, 0);
    const expandable = criterion.subCriteria.length > 0;
    const expanded = expandable && expandedCriteriaIds.has(criterion.id);
    const rowCount = expanded ? criterion.subCriteria.length : 1;

    return [
      ...blocks,
      {
        node: {
          criterion,
          expandable,
          expanded,
          x: CRITERION_COLUMN_X,
          y: rowTop(firstRow + (rowCount - 1) / 2),
          width: CRITERION_NODE_WIDTH,
        },
        children: expanded
          ? criterion.subCriteria.map((subCriterion, index) => ({
              subCriterion,
              x: SUB_CRITERION_COLUMN_X,
              y: rowTop(firstRow + index),
              width: SUB_CRITERION_NODE_WIDTH,
            }))
          : [],
        rowCount,
      },
    ];
  }, []);
}

function placeRoot(
  criteria: CriteriaWeightsCriterion[],
  blocks: CriterionBlock[],
): CriteriaWeightsTreeRootNode {
  const firstNode = blocks.at(0)?.node;
  const lastNode = blocks.at(-1)?.node;
  const weights = criteria
    .map((criterion) => criterion.weight)
    .filter((weight): weight is number => weight !== null);

  return {
    x: 0,
    y: firstNode && lastNode ? (firstNode.y + lastNode.y) / 2 : 0,
    width: ROOT_NODE_WIDTH,
    weight: weights.length > 0 ? weights.reduce((total, weight) => total + weight, 0) : null,
  };
}

function rowTop(row: number): number {
  return row * TREE_ROW_HEIGHT;
}

function toLink(
  key: string,
  parent: CriteriaWeightsTreeBox,
  child: CriteriaWeightsTreeBox,
): CriteriaWeightsTreeLink {
  const startX = parent.x + parent.width;
  const startY = parent.y + TREE_NODE_HEIGHT / 2;
  const endX = child.x;
  const endY = child.y + TREE_NODE_HEIGHT / 2;
  const middleX = (startX + endX) / 2;

  return {
    key,
    path: `M ${startX} ${startY} C ${middleX} ${startY}, ${middleX} ${endY}, ${endX} ${endY}`,
  };
}
